import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import PageTitle from '../../components/PageTitle';
import { lookupMainMeterBySubMeter } from '../../api';

const { Text } = Typography;

const heartbeatColumns = [
  { title: 'ID', dataIndex: 'id', width: 80 },
  { title: 'meter_no', dataIndex: 'meter_no', width: 140 },
  { title: 'rx_topic', dataIndex: 'rx_topic', ellipsis: true },
  { title: 'heartbeat_hex', dataIndex: 'heartbeat_hex', width: 100 },
  {
    title: 'received_at',
    dataIndex: 'received_at',
    width: 180,
    render: (v) => (v ? String(v).replace('T', ' ').slice(0, 19) : '-'),
  },
];

const mainMeterColumns = [
  { title: '设备ID', dataIndex: 'id', width: 80 },
  { title: 'device_mac', dataIndex: 'device_mac', width: 140, render: (v) => v || '-' },
  { title: '当前表号', dataIndex: 'current_meter_no', width: 160 },
  {
    title: '主副表',
    dataIndex: 'device_detail_type',
    width: 80,
    render: (v) => (Number(v) === 1 ? <Tag color="blue">主表</Tag> : String(v ?? '-')),
  },
  {
    title: '在线',
    dataIndex: 'is_online',
    width: 70,
    render: (v) => (v == null ? '-' : Number(v) === 1 ? '在线' : '离线'),
  },
  {
    title: '开关闸',
    dataIndex: 'device_status',
    width: 70,
    render: (v) => (Number(v) === 1 ? '通电' : '断电'),
  },
  { title: 'bind_room_id', dataIndex: 'bind_room_id', width: 100, render: (v) => v ?? '-' },
  { title: 'topic_mac', dataIndex: 'topic_mac', width: 140 },
  { title: 'rx_topic', dataIndex: 'rx_topic', ellipsis: true },
  { title: 'tx_topic', dataIndex: 'tx_topic', ellipsis: true },
];

const MainMeterLookup = () => {
  const [meterNo, setMeterNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const runQuery = async () => {
    const q = String(meterNo || '').trim();
    if (!q) {
      message.warning('请输入 YJ 副表当前表号');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await lookupMainMeterBySubMeter(q);
      if (!res?.success) {
        setError(res?.msg || '查询失败');
        return;
      }
      setResult(res.data || null);
      if (!res.data?.latest_rx_topic) {
        message.warning('未找到该表最近心跳，无法反查主表');
      } else if (!res.data?.main_meters?.length) {
        message.warning('已找到 RX 主题，但未匹配到主表');
      } else {
        message.success(`找到 ${res.data.main_meters.length} 个主表`);
      }
    } catch (e) {
      setError(e?.response?.data?.msg || e?.message || '查询失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageTitle title="主表查询" />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="副表 → 最新心跳主题 → 主表"
        description="输入 YJ 副表当前表号：先按设备 MAC 查 mqtt_heartbeat_raw 最近 10 条心跳，取最新 rx_topic，再反查 device_topic_mapping + device_info（device_detail_type=1）。若副表已绑定房间，优先只展示同房源内主表。"
      />

      <Card title="查询条件" size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 640 }}>
          <div>
            <Text type="secondary">YJ 副表当前表号</Text>
            <Input
              value={meterNo}
              onChange={(e) => setMeterNo(e.target.value)}
              placeholder="如 YJ88250422100921"
              disabled={loading}
              onPressEnter={runQuery}
            />
          </div>
          <Button type="primary" loading={loading} onClick={runQuery}>
            查询主表
          </Button>
        </Space>
      </Card>

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

      {result ? (
        <>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Space direction="vertical" size={4}>
              <Text>输入表号：{result.input_meter_no}</Text>
              <Text>
                副表设备：
                {result.sub_device_found
                  ? `ID ${result.sub_device?.id} / ${result.sub_device?.current_meter_no || '-'} / MAC ${result.sub_device?.device_mac || '-'}`
                  : '未在 device_info 中找到（将直接用输入表号查心跳）'}
              </Text>
              <Text type="secondary">心跳匹配键：{(result.heartbeat_keys || []).join(', ') || '-'}</Text>
              <Text>
                最新 RX 主题：
                {result.latest_rx_topic ? <Text code>{result.latest_rx_topic}</Text> : '-'}
              </Text>
              {result.house_scoped ? (
                <Tag color="green">已按同房源缩小主表范围</Tag>
              ) : null}
            </Space>
          </Card>

          <Card title="最近心跳（mqtt_heartbeat_raw，最多 10 条）" size="small" style={{ marginBottom: 16 }}>
            <Table
              rowKey="id"
              size="small"
              columns={heartbeatColumns}
              dataSource={result.recent_heartbeats || []}
              pagination={false}
              scroll={{ x: 900 }}
            />
          </Card>

          <Card title="匹配主表" size="small">
            <Table
              rowKey={(r) => `${r.id}-${r.topic_mac}`}
              size="small"
              columns={mainMeterColumns}
              dataSource={result.main_meters || []}
              pagination={false}
              scroll={{ x: 1200 }}
            />
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default MainMeterLookup;
